import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    AmericanStateOptions,
    forLifeOf,
    ignoreNullish,
    LifecycleAware,
    Masks,
    Unmasks,
    ClaimService,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, pluck, shareReplay, startWith } from 'rxjs/operators';
import {
    GetBuyerImplementationCompanyInformationResponse,
    GetBuyerImplementationCompanyInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-company-information';
import {
    GetBuyerCompanyInformationResponse,
    GetBuyerCompanyInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-company-information';
import { ManageBuyerService } from '../manage-buyer.service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'manage-buyer',
    styleUrls: ['./buyer-company-information.component.scss'],
    template: `
        <div class="row">
            <div class="col-12 col-md-8">
                <card [headerTitle]="'Buyer Information'" [style.marginBottom]="'3rem'">
                    <div card-body class="company-information-card-body">
                        <div class="company-information-controls">
                            <form
                                [formGroup]="companyInformationFormGroup"
                                (ngSubmit)="handleUpdate()"
                            >
                                <div class="row">
                                    <div class="col-12">
                                        <lsq-label
                                            label="Buyer Name*"
                                            subLabel="The name the buyer uses to do business."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="companyPreferredName"
                                            formControlName="companyPreferredName"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12">
                                        <lsq-label
                                            label="Buyer Legal Name*"
                                            subLabel="The legal name of the buyer. May be the same as Buyer Name on many occasions."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="companyLegalName"
                                            formControlName="companyLegalName"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12">
                                        <lsq-label
                                            label="Buyer External ID*"
                                            subLabel="The ID used to identify this buyer."
                                        ></lsq-label>
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="buyerExternalId"
                                            formControlName="buyerExternalId"
                                            [inputValue]="buyerExternalId | async"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12">
                                        <lsq-label
                                            label="Platform*"
                                            subLabel="Select the type of platform this buyer represents."
                                        ></lsq-label>
                                        <lsq-select
                                            [basicLabel]="true"
                                            name="platformName"
                                            [options]="[
                                                { value: '', label: '' },
                                                {
                                                    value: 'LSQ Enterprise',
                                                    label: 'LSQ Enterprise'
                                                },
                                                {
                                                    value: 'testPlatform',
                                                    label: 'testPlatform'
                                                },
                                                { value: 'LSS', label: 'LSS' },
                                                { value: 'counselLink', label: 'counselLink' },
                                                { value: 'esker', label: 'Esker' }
                                            ]"
                                            formControlName="platformName"
                                            [group]="companyInformationFormGroup"
                                        ></lsq-select>
                                    </div>
                                    <div class="col-12">
                                        <lsq-label
                                            label="Buyer Organization Type"
                                            subLabel="Select the specific organization type for this buyer."
                                        ></lsq-label>
                                        <lsq-select
                                            [basicLabel]="true"
                                            name="companyOrganizationType"
                                            [options]="[
                                                {
                                                    value: 'UNKNOWN_COMPANY_ORGANIZATION_TYPE',
                                                    label: 'Unknown Buyer Organization Type'
                                                },
                                                { value: 'CORPORATION', label: 'Corporation' },
                                                { value: 'LLC', label: 'LLC' },
                                                { value: 'PARTNERSHIP', label: 'Partnership' },
                                                {
                                                    value: 'SOLE_PROPRIETORSHIP',
                                                    label: 'Sole Proprietorship'
                                                }
                                            ]"
                                            formControlName="companyOrganizationType"
                                            [group]="companyInformationFormGroup"
                                        ></lsq-select>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12 col-md-6 order-md-1">
                                        <lsq-label
                                            label="Buyer Phone Number"
                                            subLabel="The buyer’s main 10-digit phone line."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-3">
                                        <lsq-input
                                            [basicLabel]="true"
                                            [mask]="masks.PhoneMask"
                                            type="tel"
                                            [unmask]="phoneUnmask"
                                            name="companyPhoneNumber"
                                            formControlName="companyPhoneNumber"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-2">
                                        <lsq-label
                                            label="Buyer Website"
                                            subLabel="The buyer’s main online website."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-4">
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="companyWebsite"
                                            formControlName="companyWebsite"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12 col-md-6 order-md-1">
                                        <lsq-label
                                            label="Formation Date"
                                            subLabel="The date the buyer was legally formed."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-3">
                                        <lsq-input
                                            type="date"
                                            [basicLabel]="true"
                                            [textFormatter]="formatDate"
                                            name="formationDate"
                                            formControlName="formationDate"
                                            [group]="companyInformationFormGroup"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-2">
                                        <lsq-label
                                            label="Formation State"
                                            subLabel="The state where the buyer was legally formed."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-4">
                                        <lsq-select
                                            [basicLabel]="true"
                                            name="formationState"
                                            [options]="states"
                                            formControlName="formationState"
                                            [group]="companyInformationFormGroup"
                                        ></lsq-select>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12 col-md-6 order-md-1">
                                        <lsq-label
                                            label="Standard Industrial Classification Code"
                                            subLabel="The 4-digit SIC code for the buyer."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-3">
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="sicCode"
                                            formControlName="sicCode"
                                            [group]="companyInformationFormGroup"
                                            [digitsOnly]="true"
                                            [maxLength]="4"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-2">
                                        <lsq-label
                                            label="Experian Bin"
                                            subLabel="The 9-digit number for the buyer in Experian."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-4">
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="experianBin"
                                            formControlName="experianBin"
                                            [group]="companyInformationFormGroup"
                                            [digitsOnly]="true"
                                            [maxLength]="9"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12 col-md-6 order-md-1">
                                        <lsq-label
                                            label="Duns Number"
                                            subLabel="The 9-digit number for the buyer in D&B."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-3">
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="dunsNumber"
                                            formControlName="dunsNumber"
                                            [group]="companyInformationFormGroup"
                                            [digitsOnly]="true"
                                            [maxLength]="9"
                                        >
                                        </lsq-input>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-2">
                                        <lsq-label
                                            label="Employer Identification Number (EIN)"
                                            subLabel="The 9-digit number assigned by the IRS."
                                        ></lsq-label>
                                    </div>
                                    <div class="col-12 col-md-6 order-md-4">
                                        <lsq-input
                                            [basicLabel]="true"
                                            name="ein"
                                            formControlName="ein"
                                            [group]="companyInformationFormGroup"
                                            [digitsOnly]="true"
                                            [maxLength]="9"
                                        >
                                        </lsq-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div
                                        *ngIf="!editBuyersDisabled"
                                        class="col-12 col-md-3"
                                        [style.marginTop]="'2rem'"
                                    >
                                        <lsq-button
                                            [style.marginRight]="'2rem'"
                                            appearance="secondary"
                                            size="block"
                                            [disabled]="!(hasChanges$ | async) || submitting"
                                            (buttonClick)="resetForm()"
                                        >
                                            Clear Changes
                                        </lsq-button>
                                    </div>
                                    <div
                                        *ngIf="!editBuyersDisabled"
                                        class="col-12 col-md-3"
                                        [style.marginTop]="'2rem'"
                                    >
                                        <lsq-button
                                            appearance="primary"
                                            size="block"
                                            type="submit"
                                            [submitting]="submitting"
                                            [disabled]="!(hasChanges$ | async)"
                                        >
                                            Save
                                        </lsq-button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </card>
            </div>
            <div class="col-12 col-md-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                    <p class="body text-dark-gray" [style.marginBottom]="'3rem'">
                        Please enter your updated information for the buyer and once you are done
                        click the Save button.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
@LifecycleAware()
export class BuyerCompanyInformationComponent implements OnInit {
    public states = AmericanStateOptions;
    public editBuyersDisabled = !this._claimService.isOnSync(DashboardClaim.AdminEditBuyer);
    public masks = Masks;
    public phoneUnmask = Unmasks.PhoneUnmask;
    public submitting = false;
    private _fetchSubject = new Subject();
    public buyerExternalId = this._manageBuyerService.buyerAccountDetails$.pipe(
        ignoreNullish(),
        pluck('buyerExternalId')
    );
    private _cachedResponse:
        | GetBuyerCompanyInformationResponse
        | GetBuyerImplementationCompanyInformationResponse;

    public companyInformationFormGroup = new FormGroup({
        companyLegalName: new FormControl({ value: null, disabled: true }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        companyPreferredName: new FormControl({ value: null, disabled: true }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        buyerExternalId: new FormControl({ value: null, disabled: true }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        platformName: new FormControl({ value: null, disabled: true }, [Validators.required]),
        companyOrganizationType: new FormControl({
            value: 'UNKNOWN_COMPANY_ORGANIZATION_TYPE',
            disabled: this.editBuyersDisabled
        }),
        companyPhoneNumber: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.phone
        ]),

        companyWebsite: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.maxLength(255)
        ]),
        dunsNumber: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.exactLength(9)
        ]),
        ein: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.exactLength(9),
            LsqValidators.number
        ]),
        experianBin: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.exactLength(9)
        ]),
        formationDate: new FormControl({ value: null, disabled: this.editBuyersDisabled }),
        formationState: new FormControl({ value: null, disabled: this.editBuyersDisabled }),

        servicesOverview: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.maxLength(255)
        ]),
        sicCode: new FormControl({ value: null, disabled: this.editBuyersDisabled }, [
            LsqValidators.exactLength(4),
            LsqValidators.number
        ])
    });

    public hasChanges$: Observable<boolean> = this.companyInformationFormGroup.valueChanges.pipe(
        map((_) =>
            Object.keys(this.companyInformationFormGroup.value).some((formGroupKey) => {
                if (formGroupKey === 'formationDate') {
                    return (
                        this.formatDate(this.companyInformationFormGroup.value[formGroupKey]) !==
                        this.formatDate(this._cachedResponse[formGroupKey])
                    );
                } else {
                    return (
                        this.companyInformationFormGroup.value[formGroupKey] !==
                        this._cachedResponse[formGroupKey]
                    );
                }
            })
        ),
        startWith(false),
        shareReplay(1)
    );

    constructor(
        private _getBuyerCompanyInformationService: GetBuyerCompanyInformationService,
        private _getBuyerImplementationCompanyInformationService: GetBuyerImplementationCompanyInformationService,
        private _manageBuyerService: ManageBuyerService,
        private _claimService: ClaimService,
        private _alertService: AlertService
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.isImplementation$,
            this._manageBuyerService.buyerKey$,
            this._fetchSubject.pipe(startWith(true))
        ])
            .pipe(
                forLifeOf(this),
                filter((buyer) => !!buyer)
            )
            .subscribe(async ([isImplementation, buyerKey, _]) => {
                if (isImplementation) {
                    this._cachedResponse = await this._getBuyerImplementationCompanyInformationService.executeOnce(
                        {
                            buyerImplementationKey: buyerKey
                        }
                    );
                } else {
                    this._cachedResponse = await this._getBuyerCompanyInformationService.executeOnce(
                        {
                            buyerKey
                        }
                    );
                }

                this.resetForm();
            });
    }

    public async handleUpdate() {
        this.companyInformationFormGroup.markAllAsTouched();
        if (this.companyInformationFormGroup.valid) {
            this.submitting = true;
            const currentBuyerCompanyInformation = this._cachedResponse;

            const currentBuyerAccountDetails = await this._manageBuyerService.buyerAccountDetails$
                .pipe(first())
                .toPromise();

            const changedValues = {};

            Object.keys(this.companyInformationFormGroup.controls).forEach((key) => {
                const currentControl = this.companyInformationFormGroup.controls[key];

                if (
                    currentControl.dirty &&
                    currentBuyerCompanyInformation[key] != currentControl.value
                ) {
                    changedValues[key] = currentControl.value;
                }
            });

            try {
                await this._manageBuyerService.updateBuyerImplementationCompanyInformation(
                    currentBuyerAccountDetails.buyerKey,
                    changedValues
                );

                this._fetchSubject.next();

                this._alertService.success('Buyer information successfully updated.');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (exception) {
                this._alertService.error('An error ocurred while updating.');
            }
            this.submitting = false;
        }
    }

    public formatDate(value: any): any {
        if (!!value) {
            return new Date(value).toISOString().split('T')[0];
        }
        return null;
    }

    public async resetForm(): Promise<void> {
        const currentBuyerInfo = this._cachedResponse;
        this.companyInformationFormGroup.reset({
            ...currentBuyerInfo,
            formationDate: this.formatDate(currentBuyerInfo.formationDate)
        });
    }
}
